import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Icon from '../../components/common/Icon';
import MetPeopleList from '../../components/common/MetPeopleList';
import { apiRequest, formatDate } from '../../utils';
import { EMAIL_PATTERN, parseContactsCsv } from '../../csv';
import './MemberHome.css';

const fallbackEvent = require('../../assets/images/fallback/event.png');

// Mirrors CONTACT_IMPORT_MAX on the API — a bigger file is trimmed here (and said so) rather
// than bounced by the server after the member already picked it.
const CONTACTS_MAX = 1000;

/* "My Network" — every member's screen, in two tabs.

   ★ **Met at events** is the one EVERY member has, on every business unit and every kind of
   event: the people they swapped details with by QR ([[MetPeopleList]] / GET /api/meet/mine).
   The networking QR is gated on nothing but `Event.NetworkingEnabled`, so a Hercules Trophy
   player collects these exactly as a Bravos member does — which is why this page is no longer
   ecosystem-gated, and why the tab bar only appears for the members who have a second tab.

   ★ **Invited by me** is the ecosystem half below, unchanged and still ecosystem-only: it needs
   an active CommunityRole and upcoming Labs to mean anything. A member without one is not
   bounced any more — the API's 403 (or a non-Bravos unit's {redirect}) simply leaves this tab
   out rather than throwing them off the page.

   That half lists the people the member invited who accepted (their network), plus per upcoming
   Lab a shareable payment link and — for a member holding a distributable voucher — a voucher
   invite they can either COPY as a message or have us EMAIL for them. A batch invite ("invite my
   network", or only the ticked people) emails everyone a register link for the chosen Lab.
   A CSV upload fills a SECOND list below it: uploaded contacts, which are not connections. They
   are the member's own addresses, echoed back exactly as their file had them — the API looks
   nothing up, so the screen can never show (or confirm the existence of) an account behind an
   address. Inviting one mails the signed invite link; accepting it is what moves them up into
   the network.
   Backend: GET /api/bravos/network + POST /api/bravos/network/{invite-batch,voucher-invite,
   import-contacts,remove}. */
const MyNetwork = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Which half is on screen. Arriving from a member-home "Invite my network" button carries
  // ?lab=<id> and means the invite tab; everything else opens on the tab every member has.
  const [tab, setTab] = useState(() => (
    new URLSearchParams(window.location.search).get('lab') ? 'network' : 'met'));
  const [copied, setCopied] = useState(null);
  const [selectedLab, setSelectedLab] = useState('');
  const [selectedIds, setSelectedIds] = useState({}); // connection_id -> true
  const [selectedPending, setSelectedPending] = useState({}); // uploaded email -> true
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  // The "email my voucher" compose form: which Lab it's open for + what's typed in it.
  const [voucherFor, setVoucherFor] = useState(null);
  const [voucherForm, setVoucherForm] = useState({ first_name: '', last_name: '', email: '' });
  const [voucherNotice, setVoucherNotice] = useState(null);
  const [importing, setImporting] = useState(false);
  // "Request more free tickets" — one button, one mail to our team, no grant.
  const [requesting, setRequesting] = useState(false);
  const [requestNotice, setRequestNotice] = useState(null);
  // Uploading someone else's address book is third-party personal data — the member confirms
  // it is theirs and that we may mail them before the file picker even opens.
  const [contactsConsent, setContactsConsent] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    apiRequest('GET', '/bravos/network')
      .then((res) => {
        // ★ Not being an ecosystem member is no longer an error here. A non-Bravos unit answers
        // {redirect} and a Bravos member without a community role gets a 403 (below) — in both
        // cases we keep the page and drop the invite tab, because the "met at events" tab does
        // not need either. Keyed on `connections`, which a real payload always carries.
        if (!res || !res.connections) return;
        setData(res);
        if (res.labs && res.labs.length) {
          // Arriving from a member-home "Invite my network" button carries ?lab=<id> — land the
          // batch invite on that Lab; otherwise default to the first upcoming Lab.
          const wanted = new URLSearchParams(window.location.search).get('lab');
          const match = wanted && res.labs.find(
            (l) => String(l.event_id).toLowerCase() === String(wanted).toLowerCase());
          setSelectedLab(match ? match.event_id : res.labs[0].event_id);
        }
      })
      .catch(() => { /* 403 notAuthorized — not an ecosystem member. See the note above. */ })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // `data` is only ever set from a real ecosystem payload, so its presence IS the gate.
  const isEcosystem = !!data;
  // Without the second tab there is only one answer. This guards the ?lab= deep link, which
  // would otherwise select a tab that renders nothing for them and leave a bare header.
  const activeTab = isEcosystem ? tab : 'met';
  const connections = (data && data.connections) || [];
  // Uploaded addresses that are not connections yet. Everything shown about them is what the
  // member's own file said — the API deliberately looks nothing up.
  const pending = (data && data.pending) || [];
  const labs = (data && data.labs) || [];
  const myVouchers = (data && data.my_vouchers) || [];
  // The code itself is named, not just the count: handing a voucher over by copying our message
  // is one way, but a member reads their code out on a call or types it into their own mail just
  // as often — and until now the screen never told them what it was.
  const vouchersLeftText = myVouchers
    .map((v) => (v.remaining === null
      ? t('vouchersUnlimitedWithCode', { offer: v.offer, code: v.code })
      : t('vouchersLeftWithCode', { offer: v.offer, code: v.code, count: v.remaining })))
    .join(' · ');

  // Copy a warm, ready-to-send message + the link (not a bare URL), so it drops
  // straight into WhatsApp/email and the recipient sees why they got it.
  const copyLink = (key, link, message) => {
    const text = message ? `${message}\n${link}` : link;
    const done = () => { setCopied(key); setTimeout(() => setCopied(null), 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(done);
    } else {
      done();
    }
  };

  const toggle = (id) => setSelectedIds((m) => ({ ...m, [id]: !m[id] }));
  const selectedCount = connections.filter((c) => selectedIds[c.id]).length;
  const allSelected = connections.length > 0 && selectedCount === connections.length;
  const toggleAll = () =>
    setSelectedIds(allSelected ? {} : Object.fromEntries(connections.map((c) => [c.id, true])));

  const togglePending = (email) => setSelectedPending((m) => ({ ...m, [email]: !m[email] }));
  const selectedPendingList = pending.filter((c) => selectedPending[c.email]).map((c) => c.email);
  const allPendingSelected = pending.length > 0 && selectedPendingList.length === pending.length;
  const togglePendingAll = () => setSelectedPending(
    allPendingSelected ? {} : Object.fromEntries(pending.map((c) => [c.email, true])));

  const sendBatch = (ids, after) => {
    if (!selectedLab || !ids.length || sending) return;
    setSending(true);
    setNotice(null);
    apiRequest('POST', '/bravos/network/invite-batch', {
      event_id: selectedLab, connection_ids: ids,
    })
      .then((r) => { setNotice(t('invitesSent', { n: (r && r.sent) || ids.length })); if (after) after(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('reinviteFailed') })))
      .finally(() => setSending(false));
  };

  // Invite the WHOLE network to the selected Lab (one email each) — the primary action.
  const inviteAll = () => sendBatch(connections.map((c) => c.id));
  // Optional: invite only the ticked connections.
  const inviteSelected = () =>
    sendBatch(connections.filter((c) => selectedIds[c.id]).map((c) => c.id), () => setSelectedIds({}));

  // Ask the Bravos team for more codes. Nothing is granted here — the request lands as a mail
  // (and a bell) in the back office, where raising the pool is a one-screen decision.
  const requestMoreVouchers = () => {
    if (requesting) return;
    setRequesting(true);
    setRequestNotice(null);
    apiRequest('POST', '/bravos/network/request-vouchers')
      .then(() => setRequestNotice(t('requestMoreTicketsSent')))
      .catch((err) => setRequestNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
      .finally(() => setRequesting(false));
  };

  // The second way to hand out a voucher: instead of copying the message, we email it for them.
  const openVoucherForm = (labId) => {
    setVoucherNotice(null);
    setVoucherForm({ first_name: '', last_name: '', email: '' });
    setVoucherFor(voucherFor === labId ? null : labId);
  };

  const sendVoucherEmail = (lab) => {
    if (sending) return;
    const first = voucherForm.first_name.trim();
    const last = voucherForm.last_name.trim();
    const email = voucherForm.email.trim();
    if (!first || !last) { setVoucherNotice(t('fillAllFields')); return; }
    if (!EMAIL_PATTERN.test(email)) { setVoucherNotice(t('invalidEmail')); return; }
    setSending(true);
    setVoucherNotice(null);
    apiRequest('POST', '/bravos/network/voucher-invite', {
      event_id: lab.event_id, first_name: first, last_name: last, email,
    })
      .then(() => {
        setVoucherFor(null);
        setVoucherForm({ first_name: '', last_name: '', email: '' });
        setVoucherNotice(t('voucherInviteSent', { name: first }));
      })
      .catch((err) => setVoucherNotice(t(err.message, { defaultValue: t('reinviteFailed') })))
      .finally(() => setSending(false));
  };

  // "Upload my contacts": read the CSV here, post the rows, then reload the list. The upload
  // only PARKS the addresses — nobody is mailed, and nobody is added to the network either;
  // a contact becomes a connection when they accept the invite the member sends them.
  const onContactsFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = '';                       // so picking the same file twice still fires
    if (!file || importing) return;
    setNotice(null);
    const reader = new FileReader();
    reader.onload = () => {
      const all = parseContactsCsv(reader.result);
      if (!all.length) { setNotice(t('contactsNoneFound')); return; }
      const contacts = all.slice(0, CONTACTS_MAX);
      setImporting(true);
      // The consent flag is the member confirming these are their own contacts and that we
      // may mail them an invite — the API refuses the upload without it.
      apiRequest('POST', '/bravos/network/import-contacts', { contacts, consent: true })
        .then((r) => {
          const parts = [t('contactsStaged', { n: (r && r.saved) || 0 })];
          if (r && r.skipped) parts.push(t('contactsSkipped', { n: r.skipped }));
          if (r && r.full) parts.push(t('contactsListFull', { max: (data && data.pending_max) || CONTACTS_MAX }));
          if (all.length > contacts.length) parts.push(t('contactsTruncated', { max: CONTACTS_MAX }));
          setNotice(parts.join(' '));
          load();
        })
        .catch((err) => setNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
        .finally(() => setImporting(false));
    };
    reader.onerror = () => setNotice(t('somethingWentWrong'));
    reader.readAsText(file);
  };

  // Invite uploaded contacts to the selected Lab. They get the signed invite link, so the
  // connection forms when they accept — which is also when they appear in the network above.
  const invitePending = (emails) => {
    if (!selectedLab || !emails.length || sending) return;
    setSending(true);
    setNotice(null);
    apiRequest('POST', '/bravos/network/invite-batch', {
      event_id: selectedLab, pending_emails: emails,
    })
      .then((r) => {
        setNotice(t('invitesSent', { n: (r && r.sent) || emails.length }));
        setSelectedPending({});
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('reinviteFailed') })))
      .finally(() => setSending(false));
  };

  const removePending = (c) => {
    if (removingId) return;
    if (typeof window !== 'undefined' && window.confirm
        && !window.confirm(t('removeContactConfirm', { name: c.name }))) return;
    setRemovingId(c.email);
    setNotice(null);
    apiRequest('POST', '/bravos/network/remove', { pending_email: c.email })
      .then(() => {
        setSelectedPending((m) => { const n = { ...m }; delete n[c.email]; return n; });
        load();
      })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
      .finally(() => setRemovingId(null));
  };

  // Remove a person from the network (they stay in the system, only the connection ends).
  const removeConnection = (c) => {
    if (removingId) return;
    if (typeof window !== 'undefined' && window.confirm
        && !window.confirm(t('removeFromNetworkConfirm', { name: c.name }))) return;
    setRemovingId(c.id);
    setNotice(null);
    apiRequest('POST', '/bravos/network/remove', { connection_id: c.id })
      .then(() => { setSelectedIds((m) => { const n = { ...m }; delete n[c.id]; return n; }); load(); })
      .catch((err) => setNotice(t(err.message, { defaultValue: t('somethingWentWrong') })))
      .finally(() => setRemovingId(null));
  };

  // Back to wherever they came from. NOT a hardcoded /member-home any more: this page is now
  // reached from the profile menu too, by members on units that have no member home at all.
  const goBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  return (
    <Base activeNav="home">
      <Card>
        <div className="bravos-brand">
          <div>
            <Title my="0" className="mt-1">{t('myNetwork')}</Title>
            <p className="bravos-brand__name">
              {activeTab === 'met' ? t('myNetworkMetNote') : t('myNetworkNote')}
            </p>
          </div>
          <button
            type="button"
            className="bravos-wallet__link bravos-members__back"
            onClick={goBack}
          >
            <Icon name="angle-left" /> {t('back')}
          </button>
        </div>
      </Card>

      {loading ? (
        <Card><span className="auth__spinner" /></Card>
      ) : (
        <>
          {/* Only drawn for the members who have a second tab — a one-tab tab bar is noise. */}
          {isEcosystem && (
            <div className="bravos-jointabs mx-3">
              <button type="button"
                      className={`bravos-jointab${activeTab === 'met' ? ' bravos-jointab--on' : ''}`}
                      onClick={() => setTab('met')}>
                {t('meetTabMet')}
              </button>
              <button type="button"
                      className={`bravos-jointab${activeTab === 'network' ? ' bravos-jointab--on' : ''}`}
                      onClick={() => setTab('network')}>
                {t('meetTabNetwork')}
              </button>
            </div>
          )}

          {activeTab === 'met' && <MetPeopleList />}

          {activeTab === 'network' && (
          <>
          {labs.length > 0 && (
            <Card>
              <Title my="1" className="mt-2">
                <Icon name="paper-plane" theme="primary" className="me-2" />
                {t('inviteToLab')}
              </Title>
              {vouchersLeftText && (
                <p className="bravos-q__hint mb-1" style={{ color: '#1a7f4b' }}>
                  <Icon name="ticket" className="me-1" />{vouchersLeftText}
                </p>
              )}
              {/* There is no fixed ceiling on the codes a member may hand out — but somebody
                  watching their count run down to nought has no way of knowing that. The button
                  is how we say so, and pressing it mails our team who asked and what they hold.
                  It shows even when they hold nothing yet: that is exactly when you ask. */}
              <div className="bravos-netvoucher mb-2">
                <button
                  type="button"
                  className="bravos-member__reclaim"
                  onClick={requestMoreVouchers}
                  disabled={requesting}
                >
                  <Icon name="ticket" className="me-1" />{t('requestMoreTickets')}
                </button>
                <span className="bravos-netvoucher__hint">{t('requestMoreTicketsHint')}</span>
              </div>
              {requestNotice && <p className="bravos-cal__notice">{requestNotice}</p>}
              {voucherNotice && <p className="bravos-cal__notice">{voucherNotice}</p>}
              {labs.map((l) => (
                <div key={l.event_id}>
                  <div className="bravos-member__alloc bravos-member__alloc--net">
                    <span className="bravos-member__alloc-lab">
                      <span
                        className="bravos-event__thumb"
                        style={{ backgroundImage: `url("${l.image_link || fallbackEvent}")` }}
                      />
                      <span className="bravos-member__alloc-name">
                        {l.event_name}
                        {l.date && <span className="bravos-member__alloc-type"> · {formatDate(l.date)}</span>}
                      </span>
                    </span>
                    <span className="bravos-netlab__actions">
                      <button
                        type="button"
                        className="bravos-member__reclaim"
                        onClick={() => copyLink(`lab-${l.event_id}`, l.link,
                          t('bravosInviteShareText', { event: l.event_name }))}
                      >
                        <Icon name="link" className="me-1" />
                        {copied === `lab-${l.event_id}` ? t('linkCopied') : t('sendPaymentLink')}
                      </button>
                      {l.voucher_link && (
                        <>
                          {/* Two ways to hand the voucher over: copy the message yourself… */}
                          <button
                            type="button"
                            className="bravos-member__reclaim bravos-netpick__voucher"
                            onClick={() => copyLink(`vlab-${l.event_id}`, l.voucher_link,
                              t('bravosVoucherInviteShareText', { event: l.event_name, code: l.voucher_code }))}
                          >
                            <Icon name="ticket" className="me-1" />
                            {copied === `vlab-${l.event_id}` ? t('linkCopied') : t('inviteWithVoucher')}
                          </button>
                          {/* …or let us email the same message for you. */}
                          <button
                            type="button"
                            className="bravos-member__reclaim bravos-netpick__voucher"
                            onClick={() => openVoucherForm(l.event_id)}
                          >
                            <Icon name="envelope" className="me-1" />
                            {t('emailMyVoucher')}
                          </button>
                        </>
                      )}
                    </span>
                  </div>

                  {voucherFor === l.event_id && (
                    <div className="bravos-netform">
                      <p className="bravos-netform__hint">
                        {t('emailMyVoucherHint', { event: l.event_name })}
                      </p>
                      <input
                        className="bravos-form__input mb-2"
                        placeholder={t('firstName')}
                        value={voucherForm.first_name}
                        maxLength={100}
                        onChange={(e) => setVoucherForm((f) => ({ ...f, first_name: e.target.value }))}
                      />
                      <input
                        className="bravos-form__input mb-2"
                        placeholder={t('lastName')}
                        value={voucherForm.last_name}
                        maxLength={100}
                        onChange={(e) => setVoucherForm((f) => ({ ...f, last_name: e.target.value }))}
                      />
                      <input
                        className="bravos-form__input mb-2"
                        type="email"
                        placeholder={t('email')}
                        value={voucherForm.email}
                        maxLength={120}
                        onChange={(e) => setVoucherForm((f) => ({ ...f, email: e.target.value }))}
                      />
                      <div className="bravos-netform__buttons">
                        <button
                          type="button"
                          className="bravos-member__reclaim"
                          onClick={() => setVoucherFor(null)}
                        >
                          {t('cancel')}
                        </button>
                        <button
                          type="button"
                          className="bravos-event__claim"
                          onClick={() => sendVoucherEmail(l)}
                          disabled={sending}
                        >
                          <Icon name="paper-plane" className="me-1" />
                          {sending ? t('pleaseWait') : t('send')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </Card>
          )}

          <Card>
            <Title my="1" className="mt-2">
              <Icon name="users" theme="primary" className="me-2" />
              {t('myNetwork')}
            </Title>
            <p className="bravos-q__hint mb-2">{t('growNetworkHint')}</p>

            {/* Fill the list straight from their own address book (easiest on a desktop).
                Uploading parks the addresses; it does not add anyone to the network and
                mails nobody — hence the confirmation before the file picker opens. */}
            <div className="bravos-netupload">
              <label className="bravos-netbatch__all">
                <input
                  type="checkbox"
                  checked={contactsConsent}
                  onChange={(e) => setContactsConsent(e.target.checked)}
                />
                <span>{t('uploadContactsConsent')}</span>
              </label>
              <button
                type="button"
                className="bravos-event__claim"
                onClick={() => fileRef.current && fileRef.current.click()}
                disabled={importing || !contactsConsent}
              >
                <Icon name="upload" className="me-1" />
                {importing ? t('pleaseWait') : t('uploadMyContacts')}
              </button>
              <span className="bravos-netform__hint">{t('uploadContactsHint')}</span>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                className="d-none"
                onChange={onContactsFile}
              />
            </div>

            {notice && <p className="bravos-cal__notice">{notice}</p>}

            {/* Uploaded contacts, waiting to be invited. Kept visually apart from the network
                itself: these people have not accepted anything yet. */}
            {pending.length > 0 && (
              <div className="bravos-netpending">
                <Title my="1" className="mt-2">
                  <Icon name="address-book" theme="primary" className="me-2" />
                  {t('uploadedContacts', { n: pending.length })}
                </Title>
                <p className="bravos-q__hint mb-2">{t('uploadedContactsHint')}</p>
                {labs.length > 0 && (
                  <div className="bravos-netbatch__row">
                    <label className="bravos-netbatch__all">
                      <input
                        type="checkbox"
                        checked={allPendingSelected}
                        onChange={togglePendingAll}
                      />
                      <span>{t('selectAll')}</span>
                    </label>
                    <button
                      type="button"
                      className="bravos-event__claim"
                      onClick={() => invitePending(selectedPendingList)}
                      disabled={sending || selectedPendingList.length === 0}
                    >
                      {sending ? '…' : t('inviteSelectedCount', { n: selectedPendingList.length })}
                    </button>
                  </div>
                )}
                {pending.map((c) => (
                  <div key={c.email} className="bravos-member bravos-netpick">
                    <input
                      type="checkbox"
                      className="bravos-netpick__cb"
                      checked={!!selectedPending[c.email]}
                      onChange={() => togglePending(c.email)}
                      disabled={labs.length === 0}
                      aria-label={c.name}
                    />
                    <span className="bravos-member__body">
                      <span className="bravos-member__name">{c.name}</span>
                      <span className="bravos-member__email">{c.email}</span>
                      <span className="bravos-member__email">
                        {c.invited_at
                          ? t('contactInvitedOn', { date: formatDate(c.invited_at) })
                          : t('contactNotInvitedYet')}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="bravos-member__reclaim bravos-netpick__remove"
                      onClick={() => removePending(c)}
                      disabled={removingId === c.email}
                      title={t('removeContact')}
                    >
                      {removingId === c.email ? '…' : t('removeContact')}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {connections.length === 0 ? (
              <p className="text-muted mb-0">{t('networkEmpty')}</p>
            ) : (
              <>
                {labs.length > 0 && (
                  <div className="bravos-netbatch">
                    <div className="bravos-netbatch__row">
                      <span className="bravos-member__alloc-name">{t('inviteMyNetworkTo')}</span>
                      <select
                        className="bravos-network__labselect"
                        value={selectedLab}
                        onChange={(e) => setSelectedLab(e.target.value)}
                      >
                        {labs.map((l) => (
                          <option key={l.event_id} value={l.event_id}>{l.event_name}</option>
                        ))}
                      </select>
                    </div>
                    {/* Primary: email EVERYONE in the network about the chosen Lab. */}
                    <button
                      type="button"
                      className="bravos-event__claim bravos-netbatch__primary"
                      onClick={inviteAll}
                      disabled={sending || connections.length === 0}
                    >
                      <Icon name="paper-plane" className="me-1" />
                      {sending ? '…' : t('inviteMyNetwork')}
                    </button>
                    {/* Optional: only the ticked people. */}
                    <div className="bravos-netbatch__row">
                      <label className="bravos-netbatch__all">
                        <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                        <span>{t('selectAll')}</span>
                      </label>
                      <button
                        type="button"
                        className="bravos-event__claim"
                        onClick={inviteSelected}
                        disabled={sending || selectedCount === 0}
                      >
                        {sending ? '…' : t('inviteSelectedCount', { n: selectedCount })}
                      </button>
                    </div>
                  </div>
                )}

                {connections.map((c) => (
                  <div key={c.id} className="bravos-member bravos-netpick">
                    <input
                      type="checkbox"
                      className="bravos-netpick__cb"
                      checked={!!selectedIds[c.id]}
                      onChange={() => toggle(c.id)}
                      disabled={labs.length === 0}
                      aria-label={c.name}
                    />
                    <span className="bravos-member__body">
                      <span className="bravos-member__name">
                        {c.name}
                        {c.claimed_voucher && (
                          <span className="bravos-badge bravos-badge--voucher ms-2">
                            🎟️ {t('claimedVoucherBadge')}
                          </span>
                        )}
                      </span>
                      {c.company_name && (
                        <span className="bravos-member__email">{c.company_name}</span>
                      )}
                      {c.since && (
                        <span className="bravos-member__email">
                          {t('connectedSince', { date: formatDate(c.since) })}
                        </span>
                      )}
                    </span>
                    <button
                      type="button"
                      className="bravos-member__reclaim bravos-netpick__remove"
                      onClick={() => removeConnection(c)}
                      disabled={removingId === c.id}
                      title={t('removeFromNetwork')}
                    >
                      {removingId === c.id ? '…' : t('removeFromNetwork')}
                    </button>
                  </div>
                ))}
              </>
            )}
          </Card>

          {/* Another way to bring people in — the shared refer-a-friend compose page
              (same CTA as the member home). */}
          <Card>
            <button type="button" className="bravos-memberslink"
                    onClick={() => navigate('/refer')}>
              <span className="bravos-memberslink__body">
                <span className="bravos-memberslink__title">
                  <Icon name="user-add" theme="primary" className="me-2" />
                  {t('referFriend')}
                </span>
                <span className="bravos-memberslink__count">{t('referFriendCtaSub')}</span>
              </span>
              <Icon name="angle-right" className="bravos-event__chev" />
            </button>
          </Card>
          </>
          )}
        </>
      )}
    </Base>
  );
};

export default MyNetwork;
